"use client";

import { FC, useCallback, useMemo } from "react";
import { useLocale, useTranslations } from "next-intl";

import {
  ExpertiseRecord,
  TargetGroupRecord,
  ArticleRecord,
  ServiceRecord,
  OfficeRecord,
} from "@graphql/types/graphql";
import { getLink } from "utils";

import { Teaser } from "./templates/Teaser";
import { ArticleTeaser } from "./ArticleTeaser";
import { OfficeTeaser } from "./OfficeTeaser";
import { ILink, ITag } from "@types";
import { SLUGS } from "consts";
import { Locale } from "consts/locale";

interface Props {
  pretitle?: string;
  page: ExpertiseRecord | TargetGroupRecord | ArticleRecord | ServiceRecord | OfficeRecord;
  isHorizontal?: boolean;
  isHighlighted?: boolean;
}

export const PageTeaser: FC<Props> = ({ pretitle, page, isHorizontal, isHighlighted }) => {
  const t = useTranslations("common");
  const locale = useLocale() as Locale;
  const link = useMemo(() => getLink(page, locale), [page, locale]);

  const formatTags = useCallback(
    (parentSlug: string, subServices: ServiceRecord[] | undefined): ITag[] =>
      subServices
        ?.filter((s) => s.shortTitle)
        .map((subService) => {
          const link: ILink = {
            href: SLUGS.services,
            slug: `/${parentSlug}/${subService.slug}`,
          };

          return {
            link,
            label: subService.shortTitle,
          };
        }) || [],
    [],
  );

  if (page.__typename === "ArticleRecord") {
    return (
      <ArticleTeaser article={page} isHorizontal={isHorizontal} isHighlighted={isHighlighted} />
    );
  }

  if (page.__typename === "OfficeRecord") {
    return <OfficeTeaser office={page} />;
  }

  return (
    <Teaser
      pretitle={pretitle}
      title={page.shortTitle || page.title}
      description={(page as any).shortIntro || page.intro}
      button={[
        {
          label: t("read-more"),
          link: link,
          theme: "textual",
        },
      ]}
      hasBorder
      icon={page.__typename === "ServiceRecord" ? page.icon : undefined}
      tags={page.__typename === "ServiceRecord" ? formatTags(page.slug, page.children) : undefined}
    />
  );
};
